import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { LinkIcon, SearchIcon, PlusIcon, ArrowRightIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Layout } from '@/components/Layout';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const Index = () => {
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  useSeoMeta({
    title: 'Nostr Links — Your Decentralized Link Tree',
    description: 'Create and share link lists on Nostr. No accounts, no emails — just your keys.',
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const input = searchInput.trim();
    if (!input) return;

    // Check if it's already an npub or nprofile
    if (input.startsWith('npub1') || input.startsWith('nprofile1')) {
      navigate(`/${input}`);
      return;
    }

    // Check if it's a hex pubkey
    if (/^[0-9a-f]{64}$/i.test(input)) {
      const npub = nip19.npubEncode(input);
      navigate(`/${npub}`);
      return;
    }

    // Otherwise treat as npub search
    navigate(`/${input}`);
  };

  return (
    <Layout>
      <section className="py-20 sm:py-32">
        <div className="container max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-6">
            <LinkIcon className="size-4" />
            <span>Decentralized Link Sharing</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            Your links.{' '}
            <span className="text-primary">Your keys.</span>{' '}
            Your data.
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto mb-10">
            Create beautiful, shareable link lists powered by the Nostr protocol.
            No sign-ups, no emails — just your Nostr identity.
          </p>

          <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto mb-8">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Enter npub or hex pubkey..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <Button type="submit" size="default" className="h-11">
              <ArrowRightIcon className="size-4" />
            </Button>
          </form>

          {user && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate(`/${nip19.npubEncode(user.pubkey)}`)}
                variant="default"
                size="lg"
                className="gap-2"
              >
                View My Links
                <ArrowRightIcon className="size-4" />
              </Button>
              <Button
                onClick={() => navigate('/create')}
                variant="outline"
                size="lg"
                className="gap-2"
              >
                <PlusIcon className="size-4" />
                Create New List
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-card border-y">
        <div className="container max-w-4xl">
          <h2 className="text-2xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 text-primary mb-4">
                <span className="text-xl font-bold">1</span>
              </div>
              <h3 className="font-semibold mb-2">Sign In</h3>
              <p className="text-sm text-muted-foreground">
                Use your Nostr key to sign in. No new accounts needed.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 text-primary mb-4">
                <span className="text-xl font-bold">2</span>
              </div>
              <h3 className="font-semibold mb-2">Create Lists</h3>
              <p className="text-sm text-muted-foreground">
                Add your links with titles and descriptions. Create as many lists as you want.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 text-primary mb-4">
                <span className="text-xl font-bold">3</span>
              </div>
              <h3 className="font-semibold mb-2">Share</h3>
              <p className="text-sm text-muted-foreground">
                Share your profile link with anyone. Data is stored on Nostr relays.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
